import { ChangeDetectorRef, Component, OnInit, ElementRef, ViewChild, Input } from '@angular/core';
import { MessageService } from 'primeng/api';
import { WorkflowDataService } from '../../services/workflow-data.service';
import { Chart, ChartConfiguration, ChartType, ChartData, ChartDataset } from 'chart.js/auto';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { catchError, Observable, of, tap } from 'rxjs';

// Add this interface at the top of your component file
interface jsPDFWithAutoTable extends jsPDF {
  autoTable: (options: any) => void;
}
interface MonthlyTotals {
  ecs: number;
  eltech: number;
  ses: number;
  aps: number;
  noratel: number;
  cbi: number;
  f1: number;
  total: number;
  ecsQty: number;
  eltechQty: number;
  sesQty: number;
  apsQty: number;
  noratelQty: number;
  cbiQty: number;
  f1Qty: number;
  totalQty: number;
  
}
interface MonthlyDataRow extends MonthlyTotals {
  period: string;
  isMonthHeader: boolean;
  isMonthTotal: boolean;
  isWeekRow: boolean;
}
interface CustomerMapping {
  [key: string]: keyof Pick<MonthlyTotals, 'ecs' | 'eltech' | 'ses' | 'aps' | 'noratel' | 'cbi' | 'f1'>;
}
interface WorkflowItem {
  orderId: string;
  currentStep: string;
  completed: boolean;
  uuid: string;
  poDate?: string;
  totalPrice?: string | number;
  customerName?: string;
  [key: string]: any; // for other dynamic properties
}
@Component({
  selector: 'app-quotation-weekly-dashboard',
  templateUrl: './quotation-weekly-dashboard.component.html',
  styleUrls: ['./quotation-weekly-dashboard.component.scss']
})
export class QuotationWeeklyDashboardComponent implements OnInit {
  @ViewChild('revenueChartCanvas') revenueChartCanvas!: ElementRef;
  @ViewChild('quantityChartCanvas') quantityChartCanvas!: ElementRef;
  workflowItems: WorkflowItem[] = [];
  monthlyData: MonthlyDataRow[] = [];
  isLoading: boolean = false;
  yearOptions: any[] = [];
  isSidebarCollapsed = false;
  isMobileView = false;
  isHeaderCollapsed = false;
  selectedYear: number = new Date().getFullYear();
  yearType: 'calendar' | 'financial' = 'calendar'; // Default to calendar year
  yearTypeOptions: any[] = [
    { label: 'Calendar Year', value: 'calendar' },
    { label: 'Financial Year', value: 'financial' }
  ];
  customerList = [
    { shortName: 'ECS', fullName: 'Engineering Computer Services Ltd' },
    { shortName: 'Eltech', fullName: 'Eltech Australia' },
    { shortName: 'SES', fullName: 'Specialist Electrical Solutions Pty Ltd' },
    { shortName: 'APS', fullName: 'APS Tanzania Limited' },
    { shortName: 'Noratel', fullName: 'Noratel International (Pvt) Ltd' },
    { shortName: 'CBI', fullName: 'CBI-Electric' },
    { shortName: 'F#1', fullName: 'F#1' }
  ];
  chartColors = [
    { border: '#FF0000', background: 'rgba(255, 0, 0, 0.1)' },       // Pure Red
    { border: '#0000FF', background: 'rgba(0, 0, 255, 0.1)' },       // Pure Blue
    { border: '#00FF00', background: 'rgba(0, 255, 0, 0.1)' },       // Pure Green
    { border: '#FFFF00', background: 'rgba(255, 255, 0, 0.1)' },     // Pure Yellow
    { border: '#FF00FF', background: 'rgba(255, 0, 255, 0.1)' },     // Pure Magenta
    { border: '#00FFFF', background: 'rgba(0, 255, 255, 0.1)' },     // Pure Cyan
    { border: '#FFA500', background: 'rgba(255, 165, 0, 0.1)' },     // Orange
    { border: '#800080', background: 'rgba(128, 0, 128, 0.1)' },     // Purple
    { border: '#000000', background: 'rgba(0, 0, 0, 0.1)' },         // Black
    { border: '#8B4513', background: 'rgba(139, 69, 19, 0.1)' }      // Saddle Brown
  ];
   previousYearData: {
    totalRevenue: number;
    totalOrders: number;
  } = {
    totalRevenue: 0,
    totalOrders: 0
  };


  revenueSummaryData: any[] = [];
  quantitySummaryData: any[] = [];
  monthlyRevenueTotals: number[] = [];
  monthlyQuantityTotals: number[] = [];
  accumulativeRevenueData: Array<{
    shortName: string;
    fullName: string;
    monthlyAccumulative: number[];
    finalTotal: number;
  }> = [];
  accumulativeQuantityData: Array<{
    shortName: string;
    fullName: string;
    monthlyAccumulative: number[];
    finalTotal: number;
  }> = [];
  accumulativeRevenueTotals: number[] = [];
  accumulativeQuantityTotals: number[] = [];
  yearlyTotals = {
    ecs: 0, eltech: 0, ses: 0, aps: 0, noratel: 0, cbi: 0, f1: 0, total: 0
  };
  yearlyQuantities = {
    ecs: 0, eltech: 0, ses: 0, aps: 0, noratel: 0, cbi: 0, f1: 0, total: 0
  };
  customerMapping: CustomerMapping = {
    'Engineering Computer Services Ltd': 'ecs',
    'Eltech Australia': 'eltech',
    'Specialist Electrical Solutions Pty Ltd': 'ses',
    'APS Tanzania Limited': 'aps',
    'Noratel International (Pvt) Ltd': 'noratel',
    'CBI-Electric': 'cbi',
    'F#1': 'f1'
  };
  chartLabels: string[] = [];
  revenueChartData: ChartData<'line'> = { labels: [], datasets: [] };
  quantityChartData: ChartData<'line'> = { labels: [], datasets: [] };
  revenueChart: Chart | null = null;
  quantityChart: Chart | null = null;
  displayRevenueChart: boolean = false;
  displayQuantityChart: boolean = false;
  finalMonthlyRevenueTotal: number = 0;
  finalMonthlyQuantityTotal: number = 0;
  showKikF1Upload = false;
  isUploadingKikF1 = false;
  kikF1Data: any[] = [];
  uploadResults: any = null;
  isDragOver: boolean = false;

  constructor(
    private messageService: MessageService,
    private workflowDataService: WorkflowDataService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.initializeYearOptions();
    this.loadData();
  }
  loadData(): void {
    this.workflowDataService.workflowItems$.subscribe({
      next: (items: any[]) => {
        this.workflowItems = items.map((item: any) => ({
         ...item.data,
         orderId: item.orderId,
         currentStep: item.currentStep,
         completed: item.completed,
         uuid: item.uuid,
         kikF1Detail: item.data.kikF1Detail
       }));
        this.loadAllKikF1Data().subscribe({
          next: () => {
            this.processMonthlyData();
            // Then prepare all data including charts AFTER processing
            setTimeout(() => {
             this.prepareAllData();
             console.log('Data processing complete - charts ready');
           }, 100);
          },
          error: (error) => {
            console.error('Error loading KIK#F1 data:', error);
            // Still process main data even if KIK fails
            this.processMonthlyData();
            setTimeout(() => {
              this.prepareAllData();
              console.log('Data processing complete - charts ready');
            }, 100);
          }
        });
      },
      error: () => {
        this.messageService.add({
         severity: 'error',
         summary: 'Error',
         detail: 'Failed to load workflow data from backend',
         life: 5000
       });
      }
    });
  }
  calculateAverageOrderValue(): number {
    if (this.yearlyQuantities.total === 0) return 0;
    return this.yearlyTotals.total / this.yearlyQuantities.total;
  }

initializeYearOptions(): void {
  this.yearOptions = [];
  const currentYear = new Date().getFullYear();
  
  if (this.yearType === 'calendar') {
    // Calendar years: current year to 5 years past
    for (let year = currentYear - 5; year <= currentYear; year++) {
      this.yearOptions.push({
        label: year.toString(),
        value: year
      });
    }
    this.selectedYear = currentYear; // Default to current year
  } else {
    // Financial years: April to March (e.g., 2025-2026 means Apr 2025 to Mar 2026)
    for (let year = currentYear - 2; year <= currentYear + 1; year++) {
      this.yearOptions.push({
        label: `${year}-${year + 1}`,
        value: year
      });
    }
    // Set default to current financial year (based on current month)
    const currentMonth = new Date().getMonth(); // 0-based (0 = January)
    this.selectedYear = currentMonth >= 3 ? currentYear : currentYear - 1; // April or later means current year
  }
}

  onYearTypeChange(): void {
    this.initializeYearOptions();
    this.onYearChange();
  }

  onYearChange(): void {
    this.processMonthlyData();
    const currentPeriod = this.getCurrentPeriodInfo();
    if (this.yearType === 'calendar' && this.selectedYear === currentPeriod.year) {
      this.messageService.add({
        severity: 'info',
        summary: 'Current Period',
        detail: `Showing data up to ${currentPeriod.month} Week ${currentPeriod.week}, ${currentPeriod.year}`,
        life: 4000
      });
    } else if (this.yearType === 'financial') {
      const financialYearLabel = `${this.selectedYear}-${this.selectedYear + 1}`;
      this.messageService.add({
        severity: 'info',
        summary: 'Financial Year Selected',
        detail: `Showing data for financial year ${financialYearLabel}`,
        life: 4000
      });
    }
  }

  // Override filterDataByYear to handle financial year
filterDataByYear(): any[] {
  return this.workflowItems.filter(item => {
    if (!item.poDate) return false;
    
    const poDate = new Date(item.poDate);
    const poYear = poDate.getFullYear();
    const poMonth = poDate.getMonth(); // 0-based (0 = January, 3 = April)
    
    if (this.yearType === 'calendar') {
      return poYear === this.selectedYear;
    } else {
      // Financial year: April of selectedYear to March of selectedYear + 1
      return (
        (poYear === this.selectedYear && poMonth >= 3) || // April to December of selectedYear
        (poYear === this.selectedYear + 1 && poMonth <= 2) // January to March of selectedYear + 1
      );
    }
  });
}


isCurrentWeek(row: MonthlyDataRow): boolean {
  if (!row.isWeekRow) return false;
  
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth(); // 0-based
  const currentWeek = this.getISOWeekOfMonth(currentDate);
  
  // Check if we're in the selected year period
  if (this.yearType === 'calendar' && this.selectedYear !== currentYear) return false;
  
  if (this.yearType === 'financial') {
    const financialYearStart = new Date(this.selectedYear, 3, 1); // April 1st
    const financialYearEnd = new Date(this.selectedYear + 1, 2, 31); // March 31st
    if (currentDate < financialYearStart || currentDate > financialYearEnd) return false;
  }
  
  // Find the month this week belongs to
  const rowIndex = this.monthlyData.indexOf(row);
  let monthIndex = -1;
  
  for (let i = rowIndex - 1; i >= 0; i--) {
    if (this.monthlyData[i].isMonthHeader) {
      const financialMonths = ['April', 'May', 'June', 'July', 'August', 'September',
                              'October', 'November', 'December', 'January', 'February', 'March'];
      const calendarMonths = ['January', 'February', 'March', 'April', 'May', 'June',
                             'July', 'August', 'September', 'October', 'November', 'December'];
      
      const months = this.yearType === 'financial' ? financialMonths : calendarMonths;
      monthIndex = months.indexOf(this.monthlyData[i].period);
      break;
    }
  }
  
  // Convert financial month index to calendar month for comparison
  let compareMonth = currentMonth;
  if (this.yearType === 'financial') {
    // Convert current calendar month to financial year month index
    if (currentMonth >= 3) {
      compareMonth = currentMonth - 3; // Apr(3)->0, May(4)->1, ..., Dec(11)->8
    } else {
      compareMonth = currentMonth + 9; // Jan(0)->9, Feb(1)->10, Mar(2)->11
    }
  }
  
  if (monthIndex !== compareMonth) return false;
  
  // Extract week number from period
  const weekMatch = row.period.match(/Week (\d+)/);
  if (!weekMatch) return false;
  
  const weekNumber = parseInt(weekMatch[1]);
  return weekNumber === currentWeek;
}


  processMonthlyData(): void {
    const filteredData = this.filterDataByYear();
    const monthlyStats = this.calculateMonthlyStats(filteredData);
    this.monthlyData = this.buildMonthlyDataRows(monthlyStats);
    this.calculateYearlyTotals();
    this.generateSummaryData();
    this.generateAccumulativeData();
    this.prepareAccumulativeData();
  }

calculateMonthlyStats(data: any[]): { [key: string]: any } {
  const monthlyStats: { [key: string]: any } = {};
  
  for (let month = 0; month < 12; month++) {
    const monthKey = month.toString().padStart(2, '0');
    monthlyStats[monthKey] = {
      ecs: {}, eltech: {}, ses: {}, aps: {}, noratel: {}, cbi: {}, f1: {},
      ecsQty: {}, eltechQty: {}, sesQty: {}, apsQty: {}, noratelQty: {}, cbiQty: {}, f1Qty: {}
    };

    // Calculate max weeks for this specific month
    const year = this.yearType === 'calendar' ? this.selectedYear :
      (month < 9 ? this.selectedYear : this.selectedYear + 1);
    const actualMonth = this.yearType === 'calendar' ? month :
      (month < 9 ? month + 3 : month - 9);

    const maxWeeks = this.getMaxISOWeeksInMonth(year, actualMonth);

    // Initialize up to the maximum weeks needed, including week 0
    for (let week = 0; week <= maxWeeks; week++) {
      Object.keys(this.customerMapping).forEach(customer => {
        const key = this.customerMapping[customer];
        monthlyStats[monthKey][key][week] = 0;
        monthlyStats[monthKey][`${key}Qty`][week] = 0;
      });
    }
  }

  // Process regular data items (non-KIK#F1)
  data.forEach(item => {
    const poDate = new Date(item.poDate);
    let month = poDate.getMonth();
    let mappedMonth: number;

    if (this.yearType === 'financial') {
      const itemYear = poDate.getFullYear();
      
      if (month >= 3) { // April to December
        if (itemYear === this.selectedYear) {
          mappedMonth = month - 3;
        } else {
          return;
        }
      } else { // January to March
        if (itemYear === this.selectedYear + 1) {
          mappedMonth = month + 9;
        } else {
          return;
        }
      }
      month = mappedMonth;
    } else {
      if (poDate.getFullYear() !== this.selectedYear) {
        return;
      }
    }

    const monthKey = month.toString().padStart(2, '0');
    const week = this.getISOWeekOfMonth(poDate);
    const customerKey = this.getCustomerKey(item.customerName);

    if (customerKey && monthlyStats[monthKey] && monthlyStats[monthKey][customerKey][week] !== undefined) {
      const price = parseFloat(item.totalPrice) || 0;
      monthlyStats[monthKey][customerKey][week] += price;
      monthlyStats[monthKey][`${customerKey}Qty`][week] += 1;
    }
  });

  // Process KIK#F1 data - CORRECTED VERSION
  const monthNames: { [key: string]: number } = {
    'January': 0, 'February': 1, 'March': 2, 'April': 3, 'May': 4, 'June': 5,
    'July': 6, 'August': 7, 'September': 8, 'October': 9, 'November': 10, 'December': 11
  };

  console.log('Processing KIK#F1 data...');
  
  this.kikF1Data.forEach((item: any) => {
    const poYear = parseInt(item.year);
    const monthStr = item.month;
    const monthNum = monthNames[monthStr];
    
    if (monthNum === undefined) {
      console.log(`Invalid month: ${monthStr}`);
      return;
    }

    let shouldInclude = false;
    let statsMonthIndex: number;

    if (this.yearType === 'calendar') {
      shouldInclude = poYear === this.selectedYear;
      statsMonthIndex = monthNum;
    } else { // financial year
      // CORRECTED: For financial year 2024 (April 2024 - March 2025)
      if (monthNum >= 3) { // April to December should be from this.selectedYear
        shouldInclude = poYear === this.selectedYear;
        statsMonthIndex = monthNum - 3;
      } else { // January to March should be from this.selectedYear + 1
        shouldInclude = poYear === this.selectedYear + 1;
        statsMonthIndex = monthNum + 9;
      }
    }

    if (!shouldInclude) {
      console.log(`Skipping item - Year: ${poYear}, Month: ${monthStr}, Selected Year: ${this.selectedYear}, Year Type: ${this.yearType}`);
      return;
    }

    const monthKey = statsMonthIndex.toString().padStart(2, '0');
    console.log(`Processing ${monthStr} ${poYear} -> Month Key: ${monthKey}`);

    // Convert week format to number
    let weekNumber = 0;
    const weekStr = item.week.toString().toLowerCase();
    
    if (weekStr === 'total' || weekStr === '0') {
      weekNumber = 0;
    } else if (weekStr.includes('1st') || weekStr === '1') {
      weekNumber = 1;
    } else if (weekStr.includes('2nd') || weekStr === '2') {
      weekNumber = 2;
    } else if (weekStr.includes('3rd') || weekStr === '3') {
      weekNumber = 3;
    } else if (weekStr.includes('4th') || weekStr === '4') {
      weekNumber = 4;
    } else if (weekStr.includes('5th') || weekStr === '5') {
      weekNumber = 5;
    } else if (weekStr.includes('6th') || weekStr === '6') {
      weekNumber = 6;
    } else {
      weekNumber = parseInt(item.week) || 0;
    }

    const customerKey = 'f1';
    const value = parseFloat(item.value) || 0;
    const qty = parseFloat(item.quantity) || 1;

    console.log(`Processing week ${weekNumber} for ${monthStr}: Value=${value}, Qty=${qty}`);

    // CRITICAL FIX: Handle week 0 data directly
    if (weekNumber === 0) {
      // Add directly to week 0 for month totals
      if (monthlyStats[monthKey] && monthlyStats[monthKey][customerKey]) {
        if (monthlyStats[monthKey][customerKey][0] !== undefined) {
          monthlyStats[monthKey][customerKey][0] += value;
          monthlyStats[monthKey][`${customerKey}Qty`][0] += qty;
          console.log(`Added to week 0: Month ${monthKey}, Value=${value}, Qty=${qty}`);
        } else {
          console.log(`Week 0 not initialized for month ${monthKey}`);
        }
      } else {
        console.log(`Month data not found for ${monthKey}`);
      }
      return;
    }

    // For weekly data (week 1-6)
    let week = Math.max(1, Math.min(weekNumber, 6)); // Ensure week is between 1-6
    
    if (monthlyStats[monthKey] && monthlyStats[monthKey][customerKey] &&
        monthlyStats[monthKey][customerKey][week] !== undefined) {
      monthlyStats[monthKey][customerKey][week] += value;
      monthlyStats[monthKey][`${customerKey}Qty`][week] += qty;
      console.log(`Added to week ${week}: Month ${monthKey}, Value=${value}, Qty=${qty}`);
    } else {
      console.log(`Week ${week} not found for month ${monthKey}`);
    }
  });

  console.log('Final monthlyStats:', monthlyStats);
  return monthlyStats;
}

  formatNumber(value: number): string {
    if (!value) return '0';
    const roundedValue = Math.round(value);
    return new Intl.NumberFormat('en-US').format(roundedValue);
  }


  exportData(type: 'revenue' | 'quantity'): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Export',
      detail: `Exporting ${type} data...`,
      life: 3000
    });
  }

  async exportAllData(): Promise<void> {
    try {
      this.messageService.add({
        severity: 'info',
        summary: 'Export Started',
        detail: 'Generating comprehensive PDF report...',
        life: 3000
      });

      const doc = new jsPDF('l', 'mm', 'a4') as jsPDFWithAutoTable;
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;

      // Load company logo
      const logoImg = await this.loadImage('assets/img/kikblox_logo.webp');

      // Add header with logo and company info
      this.addPDFHeader(doc, logoImg, pageWidth);

      let yPosition = 45;

      // Add report title and period info
      yPosition = this.addReportTitle(doc, yPosition, pageWidth);

      // Add summary cards
      yPosition = this.addSummaryCards(doc, yPosition, pageWidth);

      // Check if we need a new page
      if (yPosition > pageHeight - 80) {
        doc.addPage();
        this.addPDFHeader(doc, logoImg, pageWidth);
        yPosition = 45;
      }

      // Add monthly data table (Revenue)
      yPosition = this.addMonthlyDataTable(doc, yPosition);

      // Check if we need a new page for quantity table
      if (yPosition > pageHeight - 80) {
        doc.addPage();
        this.addPDFHeader(doc, logoImg, pageWidth);
        yPosition = 45;
      }

      // Add monthly quantity table immediately after revenue table
      yPosition = this.addMonthlyQuantityTable(doc, yPosition);

      // Add new page for revenue summary
      doc.addPage();
      this.addPDFHeader(doc, logoImg, pageWidth);
      yPosition = 45;

      // Add revenue summary table
      yPosition = this.addRevenueSummaryTable(doc, yPosition);

      // Add quantity summary table on same page or new page if needed
      if (yPosition > pageHeight - 100) {
        doc.addPage();
        this.addPDFHeader(doc, logoImg, pageWidth);
        yPosition = 45;
      }
      yPosition = this.addQuantitySummaryTable(doc, yPosition);

      // Add new page for accumulative data
      doc.addPage();
      this.addPDFHeader(doc, logoImg, pageWidth);
      yPosition = 45;

      // Add accumulative revenue table
      yPosition = await this.addAccumulativeRevenueTable(doc, yPosition);

      // Add accumulative quantity table
      if (yPosition > pageHeight - 100) {
        doc.addPage();
        this.addPDFHeader(doc, logoImg, pageWidth);
        yPosition = 45;
      }
      await this.addAccumulativeQuantityTable(doc, yPosition);

      // Add footer to all pages
      this.addPDFFooter(doc);

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const yearLabel = this.yearType === 'financial'
        ? `FY${this.selectedYear}-${this.selectedYear + 1}`
        : `${this.selectedYear}`;

      const filename = `Quotation_Dashboard_Report_${yearLabel}_${timestamp}.pdf`;

      // Save the PDF
      doc.save(filename);

      this.messageService.add({
        severity: 'success',
        summary: 'Export Completed',
        detail: 'PDF report generated successfully!',
        life: 5000
      });

    } catch (error) {
      console.error('PDF Export Error:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Export Failed',
        detail: 'Failed to generate PDF report. Please try again.',
        life: 5000
      });
    }
  }


  private async loadImage(src: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = src;
    });
  }

  private addPDFHeader(doc: jsPDFWithAutoTable, logoImg: string, pageWidth: number): void {
    // Professional header with optimized height
    doc.setFillColor(15, 32, 54); // Deep navy #0F2036
    doc.rect(0, 0, pageWidth, 32, 'F'); // Increased from 28 to 32mm

    // Accent line at bottom
    doc.setFillColor(64, 158, 255); // Premium blue #409EFF
    doc.rect(0, 30, pageWidth, 2, 'F'); // Adjusted position

    // Calculate logo dimensions with aspect ratio maintained
    const logoMaxWidth = 25; // Slightly increased from 22 to 25
    const logoMaxHeight = 22; // Slightly increased from 20 to 22

    // Get image properties to calculate aspect ratio
    const imgProps = doc.getImageProperties(logoImg);
    const aspectRatio = imgProps.width / imgProps.height;

    // Calculate actual logo dimensions maintaining aspect ratio
    let logoWidth = logoMaxWidth;
    let logoHeight = logoMaxWidth / aspectRatio;

    // If height exceeds max, adjust based on height instead
    if (logoHeight > logoMaxHeight) {
      logoHeight = logoMaxHeight;
      logoWidth = logoMaxHeight * aspectRatio;
    }

    // Position logo with comfortable spacing
    const logoX = 14; // Slightly increased from 12
    const logoY = 5;  // Increased from 4 for better centering

    // Add logo with calculated dimensions
    doc.addImage(logoImg, 'PNG', logoX, logoY, logoWidth, logoHeight);

    // Text positioning under logo with comfortable spacing
    const textX = logoX;
    const textStartY = logoY + logoHeight + 4; // Increased spacing from 3 to 4

    // Company subtitle with comfortable typography
    doc.setTextColor(220, 231, 255); // Light blue tint #DCE7FF
    doc.setFontSize(11); // Increased from 10 to 11
    doc.setFont('helvetica', 'bold');
    doc.text('Business Intelligence Report', textX, textStartY);

    // Right-aligned metadata with better spacing
    doc.setFontSize(9); // Increased from 8 to 9
    doc.setTextColor(176, 196, 222); // Steel blue #B0C4DE

    const currentDate = new Date().toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });

    const rightX = pageWidth - 16; // Slightly increased margin
    doc.text(`Generated: ${currentDate}`, rightX, 14, { align: 'right' }); // Better vertical positioning
    doc.text('Confidential Business Report', rightX, 23, { align: 'right' }); // Better spacing

    // Reset text color
    doc.setTextColor(0, 0, 0);
  }


  private addReportTitle(doc: jsPDFWithAutoTable, yPosition: number, pageWidth: number): number {
    const yearLabel = this.yearType === 'financial'
      ? `Financial Year ${this.selectedYear}-${this.selectedYear + 1}`
      : `Calendar Year ${this.selectedYear}`;

    // Main title with professional styling
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(28, 40, 51); // Corporate dark blue #1C2833
    doc.text('QUOTATION PERFORMANCE ANALYSIS', pageWidth / 2, yPosition, { align: 'center' });

    // Subtitle with accent color
    doc.setFontSize(13);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(52, 152, 219); // Professional blue #3498DB
    doc.text(yearLabel, pageWidth / 2, yPosition + 8, { align: 'center' });

    // Professional divider line
    doc.setDrawColor(189, 195, 199); // Light gray #BDC3C7
    doc.setLineWidth(0.5);
    doc.line(50, yPosition + 15, pageWidth - 50, yPosition + 15);

    return yPosition + 25;
  }

  private addSummaryCards(doc: jsPDFWithAutoTable, yPosition: number, pageWidth: number): number {
    const cardWidth = 62;
    const cardHeight = 28;
    const cardSpacing = 8;
    const startX = (pageWidth - (cardWidth * 4 + cardSpacing * 3)) / 2;

    // Professional color scheme for KPI cards [web:6][web:15]
    const summaryData = [
      {
        label: 'Total Order Value',
        value: this.formatCurrency(this.yearlyTotals.total),
        color: [22, 160, 133], // Professional teal #16A085
        icon: '$'
      },
      {
        label: 'Total Orders',
        value: this.yearlyQuantities.total.toString(),
        color: [52, 152, 219], // Trust blue #3498DB
        icon: '#'
      },
      {
        label: 'Avg Order Value',
        value: this.formatCurrency(this.calculateAverageOrderValue()),
        color: [230, 126, 34], // Professional orange #E67E22
        icon: '⌀'
      },
      {
        label: 'AVG Quantity per Month',
        value: this.formatNumber(this.getAvgQuantityPerMonth()),
        color: [142, 68, 173], // Corporate purple #8E44AD
        icon: '👥'
      }
    ];

    summaryData.forEach((item, index) => {
      const x = startX + index * (cardWidth + cardSpacing);

      // Card shadow effect
      doc.setFillColor(220, 220, 220);
      doc.roundedRect(x + 1, yPosition + 1, cardWidth, cardHeight, 4, 4, 'F');

      // Main card background
      doc.setFillColor(item.color[0], item.color[1], item.color[2]);
      doc.roundedRect(x, yPosition, cardWidth, cardHeight, 4, 4, 'F');

      // Subtle gradient effect (top highlight)
      doc.setFillColor(
        Math.min(255, item.color[0] + 20),
        Math.min(255, item.color[1] + 20),
        Math.min(255, item.color[2] + 20)
      );
      doc.roundedRect(x, yPosition, cardWidth, 8, 4, 4, 'F');

      // Card content
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(item.label.toUpperCase(), x + cardWidth / 2, yPosition + 10, { align: 'center' });

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(item.value, x + cardWidth / 2, yPosition + 22, { align: 'center' });
    });

    doc.setTextColor(0, 0, 0);
    return yPosition + cardHeight + 20;
  }

  private addMonthlyDataTable(doc: jsPDFWithAutoTable, yPosition: number): number {
    const displayMonths = this.getDisplayMonths();
    const headers = ['Period', ...this.customerList.map(c => c.shortName), 'Total'];

    // Prepare table data
    const tableData: any[] = [];
    this.monthlyData.forEach(row => {
      if (!row.isMonthHeader) {
        const rowData = [
          row.period,
          this.formatNumber(row.ecs),
          this.formatNumber(row.eltech),
          this.formatNumber(row.ses),
          this.formatNumber(row.aps),
          this.formatNumber(row.noratel),
          this.formatNumber(row.cbi),
          this.formatNumber(row.f1),
          this.formatNumber(row.total)
        ];
        (rowData as any).isMonthTotal = row.isMonthTotal;
        (rowData as any).isWeekRow = row.isWeekRow;
        tableData.push(rowData);
      }
    });

    // Add Annual Total footer row
    const annualTotalRow = [
      'Annual Total',
      this.formatNumber(this.yearlyTotals.ecs),
      this.formatNumber(this.yearlyTotals.eltech),
      this.formatNumber(this.yearlyTotals.ses),
      this.formatNumber(this.yearlyTotals.aps),
      this.formatNumber(this.yearlyTotals.noratel),
      this.formatNumber(this.yearlyTotals.cbi),
      this.formatNumber(this.yearlyTotals.f1),
      this.formatNumber(this.yearlyTotals.total)
    ];

    // Professional section title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(28, 40, 51);
    doc.text('Custormer Wise Weekly Order Intake', 20, yPosition);

    // Professional table styling with footer only on last page
    autoTable(doc, {
      head: [headers],
      body: tableData,
      foot: [annualTotalRow],
      startY: yPosition + 10,
      showFoot: 'lastPage', // Show footer only on last page
      theme: 'plain',
      headStyles: {
        fillColor: [52, 73, 94],
        textColor: [255, 255, 255],
        fontSize: 11,
        fontStyle: 'bold',
        halign: 'center',
        cellPadding: 5,
        lineWidth: 0.2,
        lineColor: [41, 128, 185]
      },
      bodyStyles: {
        fontSize: 9,
        halign: 'right',
        valign: 'middle',
        cellPadding: 3,
        textColor: [44, 62, 80]
      },
      footStyles: {
        fillColor: [41, 128, 185], // Professional blue for footer
        textColor: [255, 255, 255],
        fontSize: 5,
        fontStyle: 'bold',
        halign: 'right',
        cellPadding: 3,
        lineWidth: 0.3,
        lineColor: [52, 73, 94]
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: {
        0: {
          halign: 'left',
          fontStyle: 'bold',
          cellWidth: 35,
          fillColor: [248, 249, 250]
        },
        [headers.length - 1]: {
          fontStyle: 'bold',
          fillColor: [243, 156, 18],
          textColor: [255, 255, 255],
          halign: 'right',
          cellWidth: 30
        }
      },
      didParseCell: (data: any) => {
        const rowIndex = data.row.index;
        const columnIndex = data.column.index;
        const rowData = tableData[rowIndex];

        // Footer styling - only appears on last page
        if (data.row.section === 'foot') {
          data.cell.styles.fillColor = [41, 128, 185]; // Deep blue
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 11;
          data.cell.styles.lineWidth = 0.4;
          data.cell.styles.lineColor = [21, 67, 96];

          // Period column in footer
          if (columnIndex === 0) {
            data.cell.styles.halign = 'left';
            data.cell.styles.fillColor = [21, 67, 96]; // Darker blue for label
          }

          // Total column special styling
          if (columnIndex === headers.length - 1) {
            data.cell.styles.fillColor = [192, 57, 43]; // Strong red for grand total
            data.cell.styles.fontSize = 12;
            data.cell.styles.lineWidth = 0.5;
          }
        }

        // Rest of your existing styling code...
        if (data.row.section === 'head') {
          data.cell.styles.fillColor = [52, 73, 94];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 11;
          data.cell.styles.lineWidth = 0.2;
          data.cell.styles.lineColor = [41, 128, 185];
        }

        if (columnIndex === headers.length - 1 && data.row.section === 'body') {
          data.cell.styles.fillColor = [243, 156, 18];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.lineWidth = 0.2;
          data.cell.styles.lineColor = [230, 126, 34];
        }

        if (rowData && (rowData as any).isMonthTotal) {
          data.cell.styles.fillColor = [22, 160, 133];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 10;

          if (columnIndex === headers.length - 1) {
            data.cell.styles.fillColor = [17, 122, 101];
            data.cell.styles.lineWidth = 0.3;
          }
        }

        if (rowData && (rowData as any).isWeekRow && data.row.section === 'body') {
          if (columnIndex === 0) {
            data.cell.styles.fillColor = [245, 245, 245];
            data.cell.styles.fontStyle = 'normal';
          }
          if (columnIndex === headers.length - 1) {
            data.cell.styles.fillColor = [254, 249, 231];
            data.cell.styles.textColor = [44, 62, 80];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      },
      margin: { left: 15, right: 15 },
      tableWidth: 'auto'
    });

    return (doc as any).lastAutoTable.finalY + 15;
  }


  private addMonthlyQuantityTable(doc: jsPDFWithAutoTable, yPosition: number): number {
    const displayMonths = this.getDisplayMonths();
    const headers = ['Period', ...this.customerList.map(c => c.shortName), 'Total'];

    // Prepare quantity table data
    const tableData: any[] = [];
    this.monthlyData.forEach(row => {
      if (!row.isMonthHeader) {
        const rowData = [
          row.period,
          row.ecsQty || 0,
          row.eltechQty || 0,
          row.sesQty || 0,
          row.apsQty || 0,
          row.noratelQty || 0,
          row.cbiQty || 0,
          row.f1Qty || 0,
          row.totalQty || 0
        ];
        (rowData as any).isMonthTotal = row.isMonthTotal;
        (rowData as any).isWeekRow = row.isWeekRow;
        tableData.push(rowData);
      }
    });

    // Add Annual Total footer row for quantities
    const annualTotalRow = [
      'Annual Total',
      this.yearlyQuantities.ecs || 0,
      this.yearlyQuantities.eltech || 0,
      this.yearlyQuantities.ses || 0,
      this.yearlyQuantities.aps || 0,
      this.yearlyQuantities.noratel || 0,
      this.yearlyQuantities.cbi || 0,
      this.yearlyQuantities.f1 || 0,
      this.yearlyQuantities.total || 0
    ];

    // Professional section title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(28, 40, 51);
    doc.text('Custormer Wise Weekly Numbers of Order Intake', 20, yPosition);

    // Professional table styling with footer only on last page
    autoTable(doc, {
      head: [headers],
      body: tableData,
      foot: [annualTotalRow],
      startY: yPosition + 10,
      showFoot: 'lastPage', // Show footer only on last page
      theme: 'plain',
      headStyles: {
        fillColor: [230, 126, 34],
        textColor: [255, 255, 255],
        fontSize: 11,
        fontStyle: 'bold',
        halign: 'center',
        cellPadding: 5,
        lineWidth: 0.2,
        lineColor: [211, 84, 0]
      },
      bodyStyles: {
        fontSize: 9,
        halign: 'center',
        valign: 'middle',
        cellPadding: 3,
        textColor: [44, 62, 80]
      },
      footStyles: {
        fillColor: [211, 84, 0], // Darker orange for footer
        textColor: [255, 255, 255],
        fontSize: 5,
        fontStyle: 'bold',
        halign: 'center',
        cellPadding: 3,
        lineWidth: 0.3,
        lineColor: [230, 126, 34]
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: {
        0: {
          halign: 'left',
          fontStyle: 'bold',
          cellWidth: 35,
          fillColor: [248, 249, 250]
        },
        [headers.length - 1]: {
          fontStyle: 'bold',
          fillColor: [254, 242, 230],
          textColor: [44, 62, 80],
          halign: 'center',
          cellWidth: 30
        }
      },
      didParseCell: (data: any) => {
        const rowIndex = data.row.index;
        const columnIndex = data.column.index;
        const rowData = tableData[rowIndex];

        // Footer styling - only appears on last page
        if (data.row.section === 'foot') {
          data.cell.styles.fillColor = [211, 84, 0]; // Dark orange
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 11;
          data.cell.styles.lineWidth = 0.4;
          data.cell.styles.lineColor = [160, 64, 0];

          // Period column in footer
          if (columnIndex === 0) {
            data.cell.styles.halign = 'left';
            data.cell.styles.fillColor = [160, 64, 0]; // Darker orange for label
          }

          // Total column special styling
          if (columnIndex === headers.length - 1) {
            data.cell.styles.fillColor = [192, 57, 43]; // Strong red for grand total
            data.cell.styles.fontSize = 12;
            data.cell.styles.lineWidth = 0.5;
          }
        }

        // Rest of your existing styling code...
        if (data.row.section === 'head') {
          data.cell.styles.fillColor = [230, 126, 34];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 11;
          data.cell.styles.lineWidth = 0.2;
          data.cell.styles.lineColor = [211, 84, 0];
        }

        if (columnIndex === headers.length - 1 && data.row.section === 'body') {
          data.cell.styles.fillColor = [254, 242, 230];
          data.cell.styles.textColor = [44, 62, 80];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.lineWidth = 0.2;
          data.cell.styles.lineColor = [230, 126, 34];
        }

        if (rowData && (rowData as any).isMonthTotal) {
          data.cell.styles.fillColor = [230, 126, 34];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 10;

          if (columnIndex === headers.length - 1) {
            data.cell.styles.fillColor = [211, 84, 0];
            data.cell.styles.lineWidth = 0.3;
          }
        }

        if (rowData && (rowData as any).isWeekRow && data.row.section === 'body') {
          if (columnIndex === 0) {
            data.cell.styles.fillColor = [245, 245, 245];
            data.cell.styles.fontStyle = 'normal';
          }
          if (columnIndex === headers.length - 1) {
            data.cell.styles.fillColor = [254, 249, 231];
            data.cell.styles.textColor = [44, 62, 80];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      },
      margin: { left: 15, right: 15 },
      tableWidth: 'auto'
    });

    return (doc as any).lastAutoTable.finalY + 15;
  }



  private addRevenueSummaryTable(doc: jsPDFWithAutoTable, yPosition: number): number {
    const displayMonths = this.getDisplayMonths();
    const headers = ['Customer', ...displayMonths.map(m => m.shortName), 'Total'];

    const tableData = this.revenueSummaryData.map(customer => [
      customer.shortName,
      ...customer.monthlyRevenue.map((revenue: number) => this.formatNumber(revenue)),
      this.formatNumber(customer.totalRevenue)
    ]);

    // Grand totals row
    const totalsRow = ['TOTAL', ...this.monthlyRevenueTotals.map(total => this.formatNumber(total)), this.formatNumber(this.yearlyTotals.total)];
    (totalsRow as any).isGrandTotal = true;
    tableData.push(totalsRow);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(28, 40, 51);
    doc.text('Customer Wise Monthly Order Intake Summary', 20, yPosition);

    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: yPosition + 10,
      theme: 'plain',
      headStyles: {
        fillColor: [52, 152, 219], // Professional blue #3498DB
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'center',
        cellPadding: 4
      },
      bodyStyles: {
        fontSize: 9,
        halign: 'right',
        cellPadding: 3,
        textColor: [44, 62, 80]
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: {
        0: {
          halign: 'left',
          fontStyle: 'bold',
          cellWidth: 25,
          fillColor: [248, 249, 250]
        },
        [headers.length - 1]: {
          fontStyle: 'bold',
          fillColor: [241, 196, 15],
          textColor: [44, 62, 80],
          halign: 'right'
        }
      },
      didParseCell: (data: any) => {
        const rowIndex = data.row.index;
        const columnIndex = data.column.index;
        const rowData = tableData[rowIndex];

        // Total column highlighting
        if (columnIndex === headers.length - 1) {
          data.cell.styles.fillColor = [241, 196, 15];
          data.cell.styles.textColor = [44, 62, 80];
          data.cell.styles.fontStyle = 'bold';
        }

        // Grand total row
        if (rowData && (rowData as any).isGrandTotal) {
          data.cell.styles.fillColor = [52, 152, 219]; // Professional blue
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 10;

          if (columnIndex === headers.length - 1) {
            data.cell.styles.fillColor = [41, 128, 185]; // Darker blue
            data.cell.styles.lineWidth = 0.4;
          }
        }
      },
      margin: { left: 15, right: 15 }
    });

    return (doc as any).lastAutoTable.finalY + 15;
  }

  private addQuantitySummaryTable(doc: jsPDFWithAutoTable, yPosition: number): number {
    const displayMonths = this.getDisplayMonths();
    const headers = ['Customer', ...displayMonths.map(m => m.shortName), 'Total'];

    const tableData = this.quantitySummaryData.map(customer => [
      customer.shortName,
      ...customer.monthlyQuantity.map((qty: number) => qty.toString()),
      customer.totalQuantity.toString()
    ]);

    const totalsRow = ['TOTAL', ...this.monthlyQuantityTotals.map(total => total.toString()), this.yearlyQuantities.total.toString()];
    (totalsRow as any).isGrandTotal = true;
    tableData.push(totalsRow);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(28, 40, 51);
    doc.text('Customer Wise Monthly Numbers of Order Intake Summary', 20, yPosition);

    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: yPosition + 10,
      theme: 'plain',
      headStyles: {
        fillColor: [230, 126, 34], // Professional orange #E67E22
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'center',
        cellPadding: 4
      },
      bodyStyles: {
        fontSize: 9,
        halign: 'center',
        cellPadding: 3,
        textColor: [44, 62, 80]
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: {
        0: {
          halign: 'left',
          fontStyle: 'bold',
          fillColor: [248, 249, 250]
        },
        [headers.length - 1]: {
          fontStyle: 'bold',
          fillColor: [254, 242, 230] // Light orange tint
        }
      },
      didParseCell: (data: any) => {
        const rowIndex = data.row.index;
        const rowData = tableData[rowIndex];

        if (rowData && (rowData as any).isGrandTotal) {
          data.cell.styles.fillColor = [230, 126, 34];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 10;
        }
      },
      margin: { left: 20, right: 20 }
    });

    return (doc as any).lastAutoTable.finalY + 15;
  }

  private async addAccumulativeRevenueTable(doc: jsPDFWithAutoTable, yPosition: number): Promise<number> {
    const displayMonths = this.getDisplayMonths();
    const headers = ['Customer', ...displayMonths.map(m => m.shortName), 'Final Total'];

    // Prepare regular table data
    const tableData = this.accumulativeRevenueData.map(customer => [
      customer.shortName,
      ...customer.monthlyAccumulative.map((revenue: number) => this.formatNumber(revenue)),
      this.formatNumber(customer.finalTotal)
    ]);

    // Calculate totals for each month and grand total
    const monthlyTotals = displayMonths.map((_, monthIndex) =>
      this.accumulativeRevenueData.reduce((sum, customer) =>
        sum + (customer.monthlyAccumulative[monthIndex] || 0), 0)
    );

    const grandTotal = this.accumulativeRevenueData.reduce((sum, customer) =>
      sum + customer.finalTotal, 0);

    // Add total row to table data
    const totalRow = [
      'System Total',
      ...monthlyTotals.map(total => this.formatNumber(total)),
      this.formatNumber(grandTotal)
    ];

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(28, 40, 51);
    doc.text('Cumulative Order Values', 20, yPosition);

    autoTable(doc, {
      head: [headers],
      body: [...tableData, totalRow], // Include total row in body
      startY: yPosition + 10,
      theme: 'plain',
      headStyles: {
        fillColor: [22, 160, 133],
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'center',
        cellPadding: 4
      },
      bodyStyles: {
        fontSize: 9,
        halign: 'right',
        cellPadding: 3,
        textColor: [44, 62, 80]
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: {
        0: {
          halign: 'left',
          fontStyle: 'bold',
          fillColor: [248, 249, 250]
        },
        [headers.length - 1]: {
          fontStyle: 'bold',
          fillColor: [232, 246, 243]
        }
      },
      // Style the last row (total row) differently
      didParseCell: function (data) {
        // Check if this is the last row (total row)
        if (data.row.index === tableData.length) {
          data.cell.styles.fillColor = [22, 160, 133]; // Same as header color
          data.cell.styles.textColor = [255, 255, 255]; // White text
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 10;

          // Special styling for first column (System Total label)
          if (data.column.index === 0) {
            data.cell.styles.halign = 'left';
            data.cell.styles.fillColor = [28, 40, 51]; // Darker color for label
          }
        }
      },
      margin: { left: 20, right: 20 }
    });

    let currentY = (doc as any).lastAutoTable.finalY + 10;

    // Add Revenue Chart positioned higher on page
    console.log('Generating revenue chart image...');
    console.log('Revenue chart data:', this.revenueChartData);

    try {
      const revenueChartImage = await this.generateChartImage('revenue');
      if (revenueChartImage) {
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;

        // Much larger chart dimensions for maximum readability
        const margin = 5;
        const chartWidth = pageWidth - (margin * 2);
        const chartHeight = (chartWidth * 700) / 1200;

        // Check if chart fits on current page with less spacing requirement
        if (currentY + chartHeight + 15 > pageHeight - 15) {
          doc.addPage();
          this.addPDFHeader(doc, await this.loadImage('assets/img/kikblox_logo.webp'), pageWidth);
          currentY = 35;
        }

        // Add chart title with reduced spacing
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(28, 40, 51);
        currentY += 1;

        // Add large chart image positioned higher
        doc.addImage(revenueChartImage, 'PNG', margin, currentY, chartWidth, chartHeight);
        currentY += chartHeight + 20;

        console.log(`Large revenue chart added: ${chartWidth}x${chartHeight} at position ${currentY}`);
      } else {
        console.warn('Revenue chart image generation failed');
      }
    } catch (error) {
      console.error('Error adding revenue chart:', error);
    }

    return currentY;
  }



  generateRevenueChartData(): void {
    if (!this.accumulativeRevenueData || this.accumulativeRevenueData.length === 0) {
      console.log('No accumulative revenue data available');
      return;
    }

    const displayMonths = this.getDisplayMonths();
    const labels = displayMonths.map(month => month.shortName);

    const monthlyTotals = displayMonths.map((month, monthIndex) => {
      return this.accumulativeRevenueData.reduce((total, customer) => {
        return total + (customer.monthlyAccumulative[monthIndex] || 0);
      }, 0);
    });

    this.finalMonthlyRevenueTotal = monthlyTotals[monthlyTotals.length - 1] || 0;

    // Customer datasets - NO datalabels
    const datasets = this.accumulativeRevenueData.map((customer, index) => {
      return {
        label: customer.shortName,
        data: customer.monthlyAccumulative,
        borderColor: this.chartColors[index % this.chartColors.length].border,
        backgroundColor: this.chartColors[index % this.chartColors.length].background,
        fill: false,
        tension: 0.1,
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBorderWidth: 1,
        datalabels: {
          display: false // Hide labels for all customer datasets
        }
      };
    });

    // Total dataset - ONLY this shows datalabels
    const totalDataset = {
      label: 'Total (Monthly)',
      data: monthlyTotals.map(value => Math.round(value)),
      borderColor: '#000000',
      backgroundColor: 'rgba(0, 0, 0, 0.1)',
      fill: false,
      tension: 0.4,
      borderWidth: 4,
      borderDash: [5, 5],
      pointRadius: 6,
      pointHoverRadius: 8,
      pointBorderWidth: 2,
      pointBackgroundColor: '#ffffff',
      pointBorderColor: '#000000',
      datalabels: {
        display: true, // ONLY show datalabels for total dataset
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderColor: '#ffffff',
        borderRadius: 6,
        borderWidth: 1,
        font: {
          weight: 'bold',
          size: 12
        },
        color: '#ffffffff',
        align: 'top',
        offset: 8,
        formatter: (value: number) => {
          return this.formatCurrency ? this.formatCurrency(value) : value.toLocaleString();
        }
      }
    };

    // Add total dataset first so it appears prominently
    datasets.unshift(totalDataset);

    this.revenueChartData = {
      labels: labels,
      datasets: datasets
    };
  }


  generateQuantityChartData(): void {
    if (!this.accumulativeQuantityData || this.accumulativeQuantityData.length === 0) {
      return;
    }

    const displayMonths = this.getDisplayMonths();
    const labels = displayMonths.map(month => month.shortName);

    // Calculate monthly totals across all customers
    const monthlyTotals = displayMonths.map((month, monthIndex) => {
      return this.accumulativeQuantityData.reduce((total, customer) => {
        return total + (customer.monthlyAccumulative[monthIndex] || 0);
      }, 0);
    });

    // Store the final month's total (last element in monthlyTotals)
    this.finalMonthlyQuantityTotal = monthlyTotals[monthlyTotals.length - 1] || 0;

    // Customer datasets - explicitly disable datalabels
    const datasets = this.accumulativeQuantityData.map((customer, index) => {
      return {
        label: customer.shortName,
        data: customer.monthlyAccumulative,
        borderColor: this.chartColors[index % this.chartColors.length].border,
        backgroundColor: this.chartColors[index % this.chartColors.length].background,
        fill: false,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 0, // Explicitly set to 0 to disable points
        pointHoverRadius: 0, // Explicitly set to 0 to disable hover points
        datalabels: {
          display: false // Explicitly disable for customer datasets
        }
      };
    });

    // Total dataset - only this one shows datalabels
    const totalDataset = {
      label: 'Total (Monthly)',
      data: monthlyTotals,
      borderColor: '#000000',
      backgroundColor: 'rgba(0, 0, 0, 0.1)',
      fill: false,
      tension: 0.4,
      borderWidth: 3,
      borderDash: [5, 5],
      pointRadius: 4, // Increased for better visibility of labels
      pointHoverRadius: 6,
      pointBorderWidth: 2,
      datalabels: {
        display: true, // ONLY show labels for total dataset
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderColor: '#ffffff',
        borderRadius: 6,
        borderWidth: 1,
        font: {
          weight: 'bold',
          size: 14
        },
        color: '#ffffff',
        align: 'top',
        offset: 10,
        formatter: (value: number) => {
          return this.formatNumber ? this.formatNumber(value) : value;
        }
      }
    };

    datasets.unshift(totalDataset);

    this.quantityChartData = {
      labels: labels,
      datasets: datasets
    };

    console.log('Generated quantity chart data with totals:', this.quantityChartData);
    console.log('Final monthly quantity total:', this.finalMonthlyQuantityTotal);
  }

  // Add this method or update your existing data loading method
  private prepareAllData(): void {
    // Your existing data preparation code...
    this.prepareAccumulativeData();

    // Generate chart data after accumulative data is ready
    this.generateRevenueChartData();
    this.generateQuantityChartData();

    console.log('All data prepared including charts');
  }

  // Or if you have a specific method where you prepare accumulative data, add these calls there:
  private prepareAccumulativeData(): void {
    // Your existing accumulative data preparation code...

    // After preparing accumulative data, generate chart data
    setTimeout(() => {
      this.generateRevenueChartData();
      this.generateQuantityChartData();
    }, 100);
  }


  private async addAccumulativeQuantityTable(doc: jsPDFWithAutoTable, yPosition: number): Promise<number> {
    const displayMonths = this.getDisplayMonths();
    const headers = ['Customer', ...displayMonths.map(m => m.shortName), 'Final Total'];

    const tableData = this.accumulativeQuantityData.map(customer => [
      customer.shortName,
      ...customer.monthlyAccumulative.map((qty: number) => qty.toString()),
      customer.finalTotal.toString()
    ]);

    // Calculate footer totals
    const footerData = [
      'System Total',
      ...this.accumulativeQuantityTotals.map(total => total.toString()),
      this.yearlyQuantities.total.toString()
    ];

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(28, 40, 51);
    doc.text('Cumulative Order Numbers', 20, yPosition);

    autoTable(doc, {
      head: [headers],
      body: tableData,
      foot: [footerData], // Add footer with totals
      startY: yPosition + 10,
      theme: 'plain',
      headStyles: {
        fillColor: [142, 68, 173],
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'center',
        cellPadding: 4
      },
      bodyStyles: {
        fontSize: 9,
        halign: 'center',
        cellPadding: 3,
        textColor: [44, 62, 80]
      },
      footStyles: {
        fillColor: [244, 236, 247],
        textColor: [44, 62, 80],
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'center',
        cellPadding: 4
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: {
        0: {
          halign: 'left',
          fontStyle: 'bold',
          fillColor: [248, 249, 250]
        },
        [headers.length - 1]: {
          fontStyle: 'bold',
          fillColor: [244, 236, 247]
        }
      },
      showFoot: 'lastPage', // Show footer only on last page
      margin: { left: 20, right: 20 }
    });

    let currentY = (doc as any).lastAutoTable.finalY + 10;

    // Add Quantity Chart positioned higher on page
    try {
      const quantityChartImage = await this.generateChartImage('quantity');
      if (quantityChartImage) {
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;

        // Much larger chart dimensions for maximum readability
        const margin = 5;
        const chartWidth = pageWidth - (margin * 2);
        const chartHeight = (chartWidth * 700) / 1200;

        // Check if chart fits on current page with less spacing requirement
        if (currentY + chartHeight + 15 > pageHeight - 15) {
          doc.addPage();
          this.addPDFHeader(doc, await this.loadImage('assets/img/kikblox_logo.webp'), pageWidth);
          currentY = 35;
        }

        // Add chart title with reduced spacing
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(28, 40, 51);
        currentY += 1;

        // Add large chart image positioned higher
        doc.addImage(quantityChartImage, 'PNG', margin, currentY, chartWidth, chartHeight);
        currentY += chartHeight + 20;

        console.log(`Large quantity chart added: ${chartWidth}x${chartHeight} at position ${currentY}`);
      }
    } catch (error) {
      console.error('Error adding quantity chart:', error);
    }

    return currentY;
  }



  private addPDFFooter(doc: jsPDFWithAutoTable): void {
    const pageCount = (doc as any).internal.pages.length - 1;

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;

      // Professional footer with gradient effect
      doc.setFillColor(248, 249, 250); // Light background #F8F9FA
      doc.rect(0, pageHeight - 15, pageWidth, 22, 'F');

      // Accent line
      doc.setFillColor(52, 152, 219);
      doc.rect(0, pageHeight - 15, pageWidth, 2, 'F');

      // Footer content with professional styling
      doc.setTextColor(108, 117, 125); // Professional gray #6C757D
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');

      // Left side - company info
      doc.text('KIKBLOX Business Intelligence System', 20, pageHeight - 10);

      // Center - document classification
      doc.setFont('helvetica', 'bold');
      doc.text('CONFIDENTIAL BUSINESS REPORT', pageWidth / 2, pageHeight - 10, { align: 'center' });

      // Right side - page information
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 20, pageHeight - 10, { align: 'right' });

      // Additional metadata line
      doc.setFontSize(7);
      doc.setTextColor(149, 165, 166); // Lighter gray #95A5A6
      const timestamp = new Date().toISOString().split('T')[0];
      doc.text(`Generated: ${timestamp} | System Version: 2.1`, 20, pageHeight - 4);

      doc.setTextColor(0, 0, 0);
    }
  }
  // Add these methods to your main component
  async generateChartImage(chartType: 'revenue' | 'quantity'): Promise<string | null> {
    try {
      // Create a temporary container with larger dimensions for high quality
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.top = '-9999px';
      tempContainer.style.left = '-9999px';
      tempContainer.style.width = '1200px';  // Significantly increased from 800px
      tempContainer.style.height = '700px';  // Significantly increased from 500px
      tempContainer.style.background = 'white';
      document.body.appendChild(tempContainer);

      // Create canvas with much larger dimensions for excellent readability
      const canvas = document.createElement('canvas');
      canvas.width = 1200;   // Increased canvas width
      canvas.height = 700;   // Increased canvas height
      canvas.style.background = 'white';
      tempContainer.appendChild(canvas);

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        document.body.removeChild(tempContainer);
        return null;
      }

      // Fill canvas with white background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const chartData = chartType === 'revenue' ? this.revenueChartData : this.quantityChartData;

      if (!chartData || !chartData.labels || !chartData.datasets ||
        chartData.labels.length === 0 || chartData.datasets.length === 0) {
        document.body.removeChild(tempContainer);
        return null;
      }

      // Custom plugin to ensure white background
      const backgroundColorPlugin = {
        id: 'customCanvasBackgroundColor',
        beforeDraw: (chart: any) => {
          const ctx = chart.ctx;
          ctx.save();
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, chart.width, chart.height);
          ctx.restore();
        }
      };

      // Enhanced chart configuration with larger text and elements
      const config: ChartConfiguration<'line'> = {
        type: 'line',
        data: {
         labels: [...chartData.labels],
         datasets: chartData.datasets.map(dataset => ({
           ...dataset,
           data: [...(dataset.data as number[])],
           borderColor: dataset.borderColor,
           backgroundColor: dataset.backgroundColor,
           fill: false,
           tension: 0.3,
           borderWidth: 4,        // Much thicker lines for visibility
           pointRadius: 6,        // Larger point size
           pointHoverRadius: 8
         }))
       },
       plugins: [backgroundColorPlugin],
       options: {
         responsive: false,
         maintainAspectRatio: false,
         animation: { duration: 0 },
         interaction: { intersect: false },
         layout: {
           padding: {
             top: 40,      // Much larger padding
             right: 40,
             bottom: 50,
             left: 50
           }
         },
         plugins: {
           title: {
             display: true,
             text: `${chartType === 'revenue' ? 'Cumulative Order Values Analysis' : 'Cumulative Order Numbers Analysis'} - ${this.selectedYear}`,
             font: {
               size: 24,        // Much larger title
               weight: 'bold'
             },
             color: '#1e293b',
             padding: 25
           },
           legend: {
             display: true,
             position: 'top',
             labels: {
               usePointStyle: true,
               padding: 20,   // Increased padding
               font: {
                 size: 16,    // Much larger legend text
                 weight: '600'
               },
               boxWidth: 15,  // Larger legend boxes
               boxHeight: 15
             }
           }
         },
         scales: {
           x: {
             display: true,
             title: {
               display: true,
               text: 'Months',
               font: {
                 size: 18,    // Much larger axis title
                 weight: 'bold'
               },
               color: '#374151',
               padding: 15
             },
             grid: {
               display: true,
               color: 'rgba(0, 0, 0, 0.15)',
               lineWidth: 1   // Slightly thicker grid lines
             },
             ticks: {
               font: {
                 size: 14,    // Much larger tick font
                 weight: '500'
               },
               color: '#6b7280',
               maxTicksLimit: 12,
               padding: 10
             }
           },
           y: {
             display: true,
             title: {
               display: true,
               text: `${chartType === 'revenue' ? 'Revenue (USD)' : 'Quantity'}`,
               font: {
                 size: 18,
                 weight: 'bold'
               },
               color: '#374151',
               padding: 15
             },
             grid: {
               display: true,
               color: 'rgba(0, 0, 0, 0.15)',
               lineWidth: 1
             },
             ticks: {
               font: {
                 size: 14,
                 weight: '500'
               },
               color: '#6b7280',
               maxTicksLimit: 10,
               padding: 10,
               callback: (value) => {
                 return chartType === 'revenue'
                   ? this.formatCurrencyShort(value as number)
                   : this.formatNumber(value as number);
               }
             }
           }
         }
       }
      };


      const chart = new Chart(ctx, config);


      return new Promise((resolve) => {
        requestAnimationFrame(() => {
         setTimeout(() => {
           try {
             // Use high quality PNG
             const imageData = canvas.toDataURL('image/png', 1.0); // Full quality for larger size
             chart.destroy();
             document.body.removeChild(tempContainer);
             resolve(imageData);
           } catch (error) {
             console.error('Error getting canvas data URL:', error);
             chart.destroy();
             document.body.removeChild(tempContainer);
             resolve(null);
           }
         }, 150);
       });
      });


    } catch (error) {
      console.error('Error generating chart image:', error);
      return null;
    }
  }
// ISO 8601 week number calculation
  getISOWeekNumber(date: Date): number {
    // Create a copy to avoid modifying the original date
    const tempDate = new Date(date.valueOf());


    // ISO week date weeks start on Monday, so correct the day number
    const dayNum = (date.getDay() + 6) % 7;


    // Set the target to the nearest Thursday (current date + 4 - current day number)
    tempDate.setDate(tempDate.getDate() - dayNum + 3);


    // ISO 8601 week number of the year for this date
    const firstThursday = tempDate.valueOf();


    // Set the target to the first day of the year
    tempDate.setMonth(0, 1);


    // If this is not a Thursday, set the target to the next Thursday
    if (tempDate.getDay() !== 4) {
      tempDate.setMonth(0, 1 + ((4 - tempDate.getDay()) + 7) % 7);
    }


    // The week number is the number of weeks between the first Thursday of the year
    // and the Thursday in the target week
    return 1 + Math.ceil((firstThursday - tempDate.valueOf()) / 604800000); // 604800000 = milliseconds in a week
  }

// Corrected ISO week of month calculation
getISOWeekOfMonth(date: Date): number {
  const year = date.getFullYear();
  const month = date.getMonth();

  // Get the first day of the month
  const firstOfMonth = new Date(year, month, 1);
  
  // Find the Monday that starts the first week
  const firstDayWeekday = firstOfMonth.getDay(); // 0=Sunday, 6=Saturday
  let firstMonday: Date;
  
  if (firstDayWeekday === 1) { // First day is Monday
    firstMonday = new Date(firstOfMonth);
  } else {
    // Go back to the Monday of the week containing the 1st
    const daysToSubtract = firstDayWeekday === 0 ? 6 : firstDayWeekday - 1;
    firstMonday = new Date(firstOfMonth);
    firstMonday.setDate(firstOfMonth.getDate() - daysToSubtract);
  }

  // Calculate which week the given date falls in
  const daysDiff = Math.floor((date.getTime() - firstMonday.getTime()) / (24 * 60 * 60 * 1000));
  return Math.floor(daysDiff / 7) + 1;
}

// Corrected maximum weeks calculation
getMaxISOWeeksInMonth(year: number, month: number): number {
  // Get the last day of the month
  const lastDay = new Date(year, month + 1, 0);
  
  // Get the week number of the last day
  const lastWeek = this.getISOWeekOfMonth(lastDay);
  return lastWeek;
}

buildMonthlyDataRows(monthlyStats: { [key: string]: any }): MonthlyDataRow[] {
  const rows: MonthlyDataRow[] = [];
  
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const currentWeek = this.getISOWeekOfMonth(currentDate);

  let endMonth: number;
  let endWeek: number;
  let currentFYStartYear: number; // Declare the variable here at the top level

  if (this.yearType === 'calendar') {
    if (this.selectedYear === currentYear) {
      endMonth = currentMonth;
      endWeek = currentWeek;
    } else if (this.selectedYear < currentYear) {
      endMonth = 11;
      endWeek = 6;
    } else {
      return rows;
    }
  } else {
    // Financial year: April to March
    const financialYearStart = new Date(this.selectedYear, 3, 1); // April 1st
    const financialYearEnd = new Date(this.selectedYear + 1, 2, 31); // March 31st
    currentFYStartYear = currentMonth >= 3 ? currentYear : currentYear - 1; // Assign value here

    if (this.selectedYear === currentFYStartYear) {
      // Current financial year - limit to current month
      if (currentMonth >= 3) {
        // April to December of current calendar year
        endMonth = currentMonth - 3; // Apr(3)->0, May(4)->1, ..., Oct(9)->6
        endWeek = currentWeek;
      } else {
        // January to March of next calendar year
        endMonth = currentMonth + 9; // Jan(0)->9, Feb(1)->10, Mar(2)->11
        endWeek = currentWeek;
      }
    } else if (this.selectedYear < currentFYStartYear) {
      // Past financial year - show all
      endMonth = 11;
      endWeek = 6;
    } else {
      // Future financial year
      return rows;
    }
  }

  // Financial year display months: April to March
  const displayMonths = this.yearType === 'calendar' ?
    ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'] :
    ['April', 'May', 'June', 'July', 'August', 'September',
      'October', 'November', 'December', 'January', 'February', 'March'];

  displayMonths.forEach((monthName, monthIndex) => {
    if (monthIndex > endMonth) {
      return;
    }

    const monthKey = monthIndex.toString().padStart(2, '0');
    const monthData = monthlyStats[monthKey];

    // Add month header
    rows.push({
      period: monthName,
      isMonthHeader: true,
      isMonthTotal: false,
      isWeekRow: false,
      ecs: 0, eltech: 0, ses: 0, aps: 0, noratel: 0, cbi: 0, f1: 0, total: 0,
      ecsQty: 0, eltechQty: 0, sesQty: 0, apsQty: 0, noratelQty: 0, cbiQty: 0, f1Qty: 0, totalQty: 0
    });

    const monthTotals: MonthlyTotals = {
      ecs: 0, eltech: 0, ses: 0, aps: 0, noratel: 0, cbi: 0, f1: 0, total: 0,
      ecsQty: 0, eltechQty: 0, sesQty: 0, apsQty: 0, noratelQty: 0, cbiQty: 0, f1Qty: 0, totalQty: 0
    };

    // Calculate actual maximum weeks for this month
    const year = this.yearType === 'calendar' ? this.selectedYear :
      (monthIndex < 9 ? this.selectedYear + 1 : this.selectedYear);
    const actualMonth = this.yearType === 'calendar' ? monthIndex :
      (monthIndex < 9 ? monthIndex + 3 : monthIndex - 9);

    const maxPossibleWeeks = this.getMaxISOWeeksInMonth(year, actualMonth);
    let maxWeeks = maxPossibleWeeks;
    
    // For current financial year, limit weeks only for current month
    if (this.yearType === 'financial' && this.selectedYear === currentFYStartYear && monthIndex === endMonth) {
      maxWeeks = Math.min(currentWeek, maxPossibleWeeks);
    } else if (this.yearType === 'calendar' && monthIndex === endMonth) {
      maxWeeks = Math.min(endWeek, maxPossibleWeeks);
    }

    // Process weeks and add month totals - TYPE SAFE VERSION
    for (let week = 1; week <= maxWeeks; week++) {
      const weekTotals = this.calculateWeekTotals(monthData, week);

      // Type-safe way to add to month totals
      (Object.keys(weekTotals) as Array<keyof MonthlyTotals>).forEach(key => {
        if (key in monthTotals) {
          monthTotals[key] += weekTotals[key] || 0;
        }
      });

      // Add week row
      rows.push({
       period: `Week ${week}`,
       isMonthHeader: false,
       isMonthTotal: false,
       isWeekRow: true,
       ...weekTotals
     });
    }

    // Add week 0 to month totals (for KIK#F1 totals, no separate row)
    const week0Totals = this.calculateWeekTotals(monthData, 0);
    (Object.keys(week0Totals) as Array<keyof MonthlyTotals>).forEach(key => {
      if (key in monthTotals) {
        monthTotals[key] += week0Totals[key] || 0;
      }
    });

    // Add month total
    const f1Week0Value = monthData?.f1 ? (monthData.f1[0] || 0) : 0;
    if (maxWeeks > 0 || f1Week0Value > 0) {
      rows.push({
       period: `${monthName} Total`,
       isMonthHeader: false,
       isMonthTotal: true,
       isWeekRow: false,
       ...monthTotals
     });
    }
  });

  return rows;
}

calculateMonthTotals(monthData: any): any {
  const totals: any = {
    ecs: 0, eltech: 0, ses: 0, aps: 0, noratel: 0, cbi: 0, f1: 0,
    ecsQty: 0, eltechQty: 0, sesQty: 0, apsQty: 0, noratelQty: 0, cbiQty: 0, f1Qty: 0
  };
  Object.keys(this.customerMapping).forEach(customer => {
    const key = this.customerMapping[customer];
    for (let week = 0; week <= 6; week++) {
      totals[key] += monthData[key][week] || 0;
      totals[`${key}Qty`] += monthData[`${key}Qty`][week] || 0;
    }
  });
  totals.total = totals.ecs + totals.eltech + totals.ses + totals.aps + totals.noratel + totals.cbi + totals.f1;
  totals.totalQty = totals.ecsQty + totals.eltechQty + totals.sesQty + totals.apsQty + totals.noratelQty + totals.cbiQty + totals.f1Qty;
  return totals;
}
calculateWeekTotals(monthData: any, week: number): any {
    const totals: any = {};
    Object.keys(this.customerMapping).forEach(customer => {
      const key = this.customerMapping[customer];
      totals[key] = monthData[key][week] || 0;
      totals[`${key}Qty`] = monthData[`${key}Qty`][week] || 0;
    });
    totals.total = totals.ecs + totals.eltech + totals.ses + totals.aps + totals.noratel + totals.cbi + totals.f1;
    totals.totalQty = totals.ecsQty + totals.eltechQty + totals.sesQty + totals.apsQty + totals.noratelQty + totals.cbiQty + totals.f1Qty;
    return totals;
  }

calculateYearlyTotals(): void {
    this.yearlyTotals = {
      ecs: 0, eltech: 0, ses: 0, aps: 0, noratel: 0, cbi: 0, f1: 0, total: 0
    };
    this.yearlyQuantities = {
      ecs: 0, eltech: 0, ses: 0, aps: 0, noratel: 0, cbi: 0, f1: 0, total: 0
    };
    this.monthlyData.forEach(row => {
      if (row.isMonthTotal) {
        this.yearlyTotals.ecs += row.ecs || 0;
        this.yearlyTotals.eltech += row.eltech || 0;
        this.yearlyTotals.ses += row.ses || 0;
        this.yearlyTotals.aps += row.aps || 0;
        this.yearlyTotals.noratel += row.noratel || 0;
        this.yearlyTotals.cbi += row.cbi || 0;
        this.yearlyTotals.f1 += row.f1 || 0;
        this.yearlyTotals.total += row.total || 0;
        this.yearlyQuantities.ecs += row.ecsQty || 0;
        this.yearlyQuantities.eltech += row.eltechQty || 0;
        this.yearlyQuantities.ses += row.sesQty || 0;
        this.yearlyQuantities.aps += row.apsQty || 0;
        this.yearlyQuantities.noratel += row.noratelQty || 0;
        this.yearlyQuantities.cbi += row.cbiQty || 0;
        this.yearlyQuantities.f1 += row.f1Qty || 0;
        this.yearlyQuantities.total += row.totalQty || 0;
      }
    });
    console.log('Monthly Data:', this.monthlyData);
    console.log('Yearly Totals:', this.yearlyTotals);
    console.log('Yearly Quantities:', this.yearlyQuantities);
  }

getWeekOfMonth(date: Date): number {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const firstDayWeekday = firstDay.getDay();
    const dayOfMonth = date.getDate();
    return Math.ceil((dayOfMonth + firstDayWeekday) / 7);
  }


  getCurrentPeriodInfo(): { month: string, week: number, year: number } {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const currentWeek = this.getWeekOfMonth(currentDate);
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return {
      month: monthNames[currentMonth],
      week: currentWeek,
      year: currentYear
    };
  }

getCustomerKey(customerName: string): string | null {
    return this.customerMapping[customerName] || null;
  }


  formatCurrency(value: number): string {
    if (!value) return '0';
    const roundedValue = Math.round(value);
    return new Intl.NumberFormat('en-US').format(roundedValue);
  }


getDisplayMonths(): any[] {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth(); // 0-based

  const allMonths = [
    { shortName: 'Jan', fullName: 'January' },
    { shortName: 'Feb', fullName: 'February' },
    { shortName: 'Mar', fullName: 'March' },
    { shortName: 'Apr', fullName: 'April' },
    { shortName: 'May', fullName: 'May' },
    { shortName: 'Jun', fullName: 'June' },
    { shortName: 'Jul', fullName: 'July' },
    { shortName: 'Aug', fullName: 'August' },
    { shortName: 'Sep', fullName: 'September' },
    { shortName: 'Oct', fullName: 'October' },
    { shortName: 'Nov', fullName: 'November' },
    { shortName: 'Dec', fullName: 'December' }
  ];

  // Financial year months: April to March
  const financialMonths = [
    { shortName: 'Apr', fullName: 'April' },    // Month 0 of FY
    { shortName: 'May', fullName: 'May' },      // Month 1 of FY
    { shortName: 'Jun', fullName: 'June' },     // Month 2 of FY
    { shortName: 'Jul', fullName: 'July' },     // Month 3 of FY
    { shortName: 'Aug', fullName: 'August' },   // Month 4 of FY
    { shortName: 'Sep', fullName: 'September' }, // Month 5 of FY
    { shortName: 'Oct', fullName: 'October' },  // Month 6 of FY
    { shortName: 'Nov', fullName: 'November' }, // Month 7 of FY
    { shortName: 'Dec', fullName: 'December' }, // Month 8 of FY
    { shortName: 'Jan', fullName: 'January' },  // Month 9 of FY
    { shortName: 'Feb', fullName: 'February' }, // Month 10 of FY
    { shortName: 'Mar', fullName: 'March' }     // Month 11 of FY
  ];

  if (this.yearType === 'calendar') {
    if (this.selectedYear === currentYear) {
      return allMonths.slice(0, currentMonth + 1);
    } else if (this.selectedYear < currentYear) {
      return allMonths;
    } else {
      return [];
    }
  } else {
    // Financial year logic: April to March
    const financialYearStart = new Date(this.selectedYear, 3, 1); // April 1st of selectedYear
    const financialYearEnd = new Date(this.selectedYear + 1, 2, 31); // March 31st of selectedYear + 1

    if (currentDate >= financialYearStart && currentDate <= financialYearEnd) {
      // We're in the current financial year
      let endMonthIndex: number;

      if (currentMonth >= 3) {
        // April to December of current calendar year
        // Add 1 to include current month in slice
        endMonthIndex = currentMonth - 3 + 1; // Apr(3)->1, May(4)->2, ..., Oct(9)->7, Dec(11)->9
      } else {
        // January to March of next calendar year
        // Add 1 to include current month in slice
        endMonthIndex = currentMonth + 9 + 1; // Jan(0)->10, Feb(1)->11, Mar(2)->12
      }

      return financialMonths.slice(0, endMonthIndex);
    } else if (this.selectedYear < currentYear ||
      (this.selectedYear === currentYear && currentMonth >= 3)) {
      // Past financial year - show all months
      return financialMonths;
    } else {
      // Future financial year
      return [];
    }
  }
}

 generateSummaryData(): void {
    const displayMonths = this.getDisplayMonths();
    const monthCount = displayMonths.length;
    this.revenueSummaryData = [];
    this.quantitySummaryData = [];
    this.monthlyRevenueTotals = new Array(monthCount).fill(0);
    this.monthlyQuantityTotals = new Array(monthCount).fill(0);
    this.customerList.forEach(customer => {
      const customerKey = this.customerMapping[customer.fullName];
      if (customerKey) {
        const monthlyRevenue = new Array(monthCount).fill(0);
        const monthlyQuantity = new Array(monthCount).fill(0);
        let totalRevenue = 0;
        let totalQuantity = 0;
        displayMonths.forEach((month, monthIndex) => {
          const monthlyTotalRows = this.monthlyData.filter(row =>
            row.isMonthTotal && row.period.startsWith(month.fullName)
          );
          if (monthlyTotalRows.length > 0) {
            const monthRow = monthlyTotalRows[0];
            monthlyRevenue[monthIndex] = monthRow[customerKey] || 0;
            monthlyQuantity[monthIndex] = monthRow[`${customerKey}Qty`] || 0;
          }
          totalRevenue += monthlyRevenue[monthIndex];
          totalQuantity += monthlyQuantity[monthIndex];
          this.monthlyRevenueTotals[monthIndex] += monthlyRevenue[monthIndex];
          this.monthlyQuantityTotals[monthIndex] += monthlyQuantity[monthIndex];
        });
        this.revenueSummaryData.push({
          shortName: customer.shortName,
          fullName: customer.fullName,
          monthlyRevenue: monthlyRevenue,
          totalRevenue: totalRevenue
        });
        this.quantitySummaryData.push({
          shortName: customer.shortName,
          fullName: customer.fullName,
          monthlyQuantity: monthlyQuantity,
          totalQuantity: totalQuantity
        });
      }
    });
  }

  exportSummary(type: 'revenue' | 'quantity'): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Export Started',
      detail: `Exporting ${type} summary data...`,
      life: 3000
    });
  }
  generateAccumulativeData(): void {
    try {
      console.log('Generating accumulative data...');
      const displayMonths = this.getDisplayMonths();
      const monthCount = displayMonths.length;
      this.accumulativeRevenueData = [];
      this.accumulativeRevenueTotals = new Array(monthCount).fill(0);
      this.accumulativeQuantityData = [];
      this.accumulativeQuantityTotals = new Array(monthCount).fill(0);
      this.customerList.forEach(customer => {
        const customerKey = this.customerMapping[customer.fullName];
        if (customerKey) {
          const accumulativeRevenue: number[] = new Array(monthCount).fill(0);
          const accumulativeQuantity: number[] = new Array(monthCount).fill(0);
          let runningRevenueTotal = 0;
          let runningQuantityTotal = 0;
          displayMonths.forEach((month, monthIndex) => {
            const monthlyTotalRows = this.monthlyData.filter(row =>
              row.isMonthTotal && row.period.startsWith(month.fullName)
            );
            if (monthlyTotalRows.length > 0) {
              const monthRow = monthlyTotalRows[0];
              const monthRevenue = Number(monthRow[customerKey]) || 0;
              const monthQuantity = Number(monthRow[`${customerKey}Qty`]) || 0;
              if (isNaN(monthRevenue) || isNaN(monthQuantity)) {
                console.warn(`Invalid data for ${customer.shortName} in ${month.fullName}: revenue=${monthRevenue}, quantity=${monthQuantity}`);
              }
              runningRevenueTotal += monthRevenue;
              runningQuantityTotal += monthQuantity;
              accumulativeRevenue[monthIndex] = runningRevenueTotal;
              accumulativeQuantity[monthIndex] = runningQuantityTotal;
            } else {
              accumulativeRevenue[monthIndex] = runningRevenueTotal;
              accumulativeQuantity[monthIndex] = runningQuantityTotal;
            }
            this.accumulativeRevenueTotals[monthIndex] += accumulativeRevenue[monthIndex];
            this.accumulativeQuantityTotals[monthIndex] += accumulativeQuantity[monthIndex];
          });
          this.accumulativeRevenueData.push({
            shortName: customer.shortName,
            fullName: customer.fullName,
            monthlyAccumulative: accumulativeRevenue,
            finalTotal: runningRevenueTotal
          });
          this.accumulativeQuantityData.push({
            shortName: customer.shortName,
            fullName: customer.fullName,
            monthlyAccumulative: accumulativeQuantity,
            finalTotal: runningQuantityTotal
          });
        }
      });
      console.log('Accumulative Revenue Data:', JSON.stringify(this.accumulativeRevenueData));
      console.log('Accumulative Quantity Data:', JSON.stringify(this.accumulativeQuantityData));
    } catch (error) {
      console.error('Error in generateAccumulativeData:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Data Error',
        detail: 'Failed to generate accumulative data.',
        life: 5000
      });
    }
  }

  exportAccumulative(type: 'revenue' | 'quantity'): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Export Started',
      detail: `Exporting accumulative ${type} data...`,
      life: 3000
    });
  }

  showRevenueChart(): void {
    this.prepareChartData('revenue');
    this.displayRevenueChart = true;
  }

  showQuantityChart(): void {
    this.prepareChartData('quantity');
    this.displayQuantityChart = true;
  }

  onRevenueChartClose(): void {
    this.displayRevenueChart = false;
  }

  onQuantityChartClose(): void {
    this.displayQuantityChart = false;
  }

  prepareChartData(type: 'revenue' | 'quantity'): void {
    const displayMonths = this.getDisplayMonths();
    this.chartLabels = displayMonths.map(month => month.shortName);
    const datasets: ChartDataset<'line'>[] = [];
    const sourceData = type === 'revenue' ? this.accumulativeRevenueData : this.accumulativeQuantityData;
    sourceData.forEach((customer, index) => {
      const colorIndex = index % this.chartColors.length;
      datasets.push({
        label: customer.shortName,
        data: customer.monthlyAccumulative,
        borderColor: this.chartColors[colorIndex].border,
        backgroundColor: this.chartColors[colorIndex].background,
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: this.chartColors[colorIndex].border,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: this.chartColors[colorIndex].border,
        pointHoverBorderWidth: 3
      });
    });
    if (type === 'revenue') {
      this.revenueChartData = {
        labels: this.chartLabels,
        datasets: datasets
      };
    } else {
      this.quantityChartData = {
        labels: this.chartLabels,
        datasets: datasets
      };
    }
  }

  onExportChart(type: 'revenue' | 'quantity'): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Export Successful',
      detail: `${type === 'revenue' ? 'Revenue' : 'Quantity'} chart exported successfully!`,
      life: 3000
    });
  }

  formatCurrencyShort(value: number): string {
    const roundedValue = Math.round(value);
    if (roundedValue >= 1000000) {
      return `$ ${Math.round(roundedValue / 1000000)}M`;
    } else if (roundedValue >= 1000) {
      return `$ ${Math.round(roundedValue / 1000)}K`;
    }
    return new Intl.NumberFormat('en-US').format(roundedValue);
  }
  getAvgQuantityPerMonth(): number {
    if (this.yearlyQuantities.total === 0) return 0;

    const currentMonths = this.getCurrentMonthsInYear();
    return Math.round((this.yearlyQuantities.total / currentMonths) * 100) / 100; // Round to 2 decimal places
  }

  private getCurrentMonthsInYear(): number {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-based

    // If selected year is future year, return 1 to avoid issues
    if (this.selectedYear > currentYear) return 1;

    // If selected year is past year, return 12 months
    if (this.selectedYear < currentYear) return 12;

    // Current year calculations
    if (this.yearType === 'calendar') {
      return currentMonth + 1; // January = 1, February = 2, etc.
    } else {
      // Financial year (April to March)
      const isInCurrentFinancialYear =
        (currentMonth >= 3) ? // April onwards in same year
          true :
          (this.selectedYear === currentYear - 1); // Jan-Mar in next year

      if (!isInCurrentFinancialYear) return 12;

      return currentMonth >= 3 ?
        currentMonth - 3 + 1 : // April to December
        9 + currentMonth + 1; // January to March (9 months from previous year + current months)
    }
  }
  onHeaderCollapse(isCollapsed: boolean) {
    this.isHeaderCollapsed = isCollapsed;
    console.log('Header collapsed:', isCollapsed);
    console.log('Header state changed:', this.isHeaderCollapsed);
  }
  
loadAllKikF1Data(): Observable<any> {
  return this.workflowDataService.getAllKikF1Data().pipe(
    tap((response) => {
      if (response.kikF1Detail && response.kikF1Detail.length > 0) {
        this.kikF1Data = response.kikF1Detail;
        console.log(`Loaded ${response.totalRecords} KIK#F1 records:`, this.kikF1Data);
      } else {
        console.log('No KIK#F1 data found');
      }
    }),
    catchError((error) => {
      console.error('Failed to load KIK#F1 data:', error);
      // Optionally show user-friendly message
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: 'No KIK#F1 data found',
        life: 3000
      });
      return of(null);
    })
  );
}

  /**
   * Show KIK#F1 upload dialog
   */
  showKikF1UploadDialog(): void {
    this.showKikF1Upload = true;
    this.uploadResults = null;
  }
/**
 * Handle KIK#F1 Excel file upload
 */
onKikF1FileUpload(event: any): void {
  const file = event.files[0];
  if (!file) return;

  // Validate file type
  if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
    this.messageService.add({
      severity: 'error',
      summary: 'Invalid File',
      detail: 'Please select an Excel file (.xlsx or .xls)',
      life: 5000
    });
    return;
  }

  // Use the extracted processing method
  this.processExcelFile(file);
}
// Drag and Drop event handlers
onDragOver(event: DragEvent): void {
  event.preventDefault();
  event.stopPropagation();
  this.isDragOver = true;
}

onDragLeave(event: DragEvent): void {
  event.preventDefault();
  event.stopPropagation();
  this.isDragOver = false;
}

onDrop(event: DragEvent): void {
  event.preventDefault();
  event.stopPropagation();
  this.isDragOver = false;

  const files = event.dataTransfer?.files;
  if (!files || files.length === 0) {
    return;
  }

  const file = files[0];
  
  // Validate file type
  if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
    this.messageService.add({
      severity: 'error',
      summary: 'Invalid File',
      detail: 'Please select an Excel file (.xlsx or .xls)',
      life: 5000
    });
    return;
  }

  // Validate file size (10MB limit)
  if (file.size > 10000000) {
    this.messageService.add({
      severity: 'error',
      summary: 'File Too Large',
      detail: 'File size must be less than 10MB',
      life: 5000
    });
    return;
  }

  // Process the dropped file
  this.processExcelFile(file);
}

// Extract the file processing logic into a separate method
private processExcelFile(file: File): void {
  console.log('Processing Excel file:', file.name);
  this.isUploadingKikF1 = true;

  // Read Excel file using FileReader
  const reader = new FileReader();
  
  reader.onload = (e: any) => {
    try {
      // Parse Excel data
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      
      // Get first worksheet
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      // Convert to JSON
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
        header: 1, // Use array format
        defval: '' // Default value for empty cells
      });
      
      console.log('Excel data parsed:', jsonData);
      
      // Process the data into your required format
      const processedData = this.parseKikF1ExcelData(jsonData);
      
      if (processedData.length === 0) {
        this.isUploadingKikF1 = false;
        this.messageService.add({
          severity: 'error',
          summary: 'No Data Found',
          detail: 'No valid KIK#F1 data found in the Excel file',
          life: 5000
        });
        return;
      }
      
      // Send processed data to backend
      this.sendKikF1DataToBackend(processedData, null);
      
    } catch (error) {
      this.isUploadingKikF1 = false;
      console.error('Excel parsing error:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Parse Error',
        detail: 'Failed to parse Excel file. Please check the file format.',
        life: 5000
      });
    }
  };
  
  reader.onerror = () => {
    this.isUploadingKikF1 = false;
    this.messageService.add({
      severity: 'error',
      summary: 'File Read Error',
      detail: 'Failed to read the Excel file',
      life: 5000
    });
  };
  
  // Read file as array buffer
  reader.readAsArrayBuffer(file);
}


/**
 * Parse KIK#F1 Excel data - Updated for column-based format
 */
parseKikF1ExcelData(jsonData: any[]): any[] {
  const processedData: any[] = [];
  
  if (jsonData.length === 0) {
    return processedData;
  }
  
  // Skip header row (index 0)
  for (let i = 1; i < jsonData.length; i++) {
    const row = jsonData[i];
    
    // Skip empty rows
    if (!row || row.length === 0) continue;
    
    // Extract data from columns based on your Excel structure
    // [Customer Name, Year, Month, Week, Value, Quantity]
    const customerName = row[0] ? row[0].toString().trim() : '';
    const year = row[1] ? parseInt(row[1]) : null;
    const month = row[2] ? row[2].toString().trim() : '';
    const week = row[3] ? row[3].toString().trim() : '';
    const value = row[4] ? parseFloat(row[4].toString().replace(/[,$]/g, '')) : null;
    const quantity = row[5] ? parseFloat(row[5].toString().replace(/[,$]/g, '')) : null;
    
    // Validate required fields
    if (!customerName || !year || !month || !week || value === null || quantity === null) {
      console.warn(`Skipping row ${i + 1}: Missing required data`, {
        customerName, year, month, week, value, quantity
      });
      continue;
    }
    
    // Validate year
    if (isNaN(year) || year < 2020 || year > 2030) {
      console.warn(`Skipping row ${i + 1}: Invalid year ${year}`);
      continue;
    }
    
    // Validate numeric values
    if (isNaN(value) || isNaN(quantity)) {
      console.warn(`Skipping row ${i + 1}: Invalid numeric values`, { value, quantity });
      continue;
    }
    
    // Create record
    const record = {
      customerName: customerName,
      year: year,
      month: month,
      week: week,
      value: value,
      quantity: quantity
    };
    
    processedData.push(record);
  }
  
  console.log('Processed KIK#F1 data:', processedData);
  return processedData;
}

/**
 * Check if record has all required fields
 */
isRecordComplete(record: any): boolean {
  return record.customerName && record.year && record.month && 
         record.week && record.value !== null && record.quantity !== null;
}

/**
 * Check if string is a month name
 */
isMonthName(str: string): boolean {
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'];
  return months.includes(str);
}
/**
 * Clear file upload safely
 */
clearFileUpload(event: any): void {
  try {
    if (event && typeof event.clear === 'function') {
      event.clear();
    }
  } catch (error) {
    console.warn('Could not clear file upload:', error);
  }
}
/**
 * Send processed data to backend
 */
sendKikF1DataToBackend(data: any[], event: any): void {
  this.workflowDataService.uploadKikF1Data(data).subscribe({
    next: (response) => {
      this.isUploadingKikF1 = false;
      this.uploadResults = response;
      
      const message = response.insertedRecords > 0 && response.updatedRecords > 0 
        ? `Successfully processed ${response.processedRecords} records (${response.insertedRecords} new, ${response.updatedRecords} updated)`
        : response.insertedRecords > 0 
        ? `Successfully inserted ${response.insertedRecords} new records`
        : `Successfully updated ${response.updatedRecords} existing records`;

      this.messageService.add({
        severity: 'success',
        summary: 'Upload Successful',
        detail: message,
        life: 5000
      });

      // Reload data to include updated KIK#F1 data
      this.loadAllKikF1Data();
      
      // Clear file upload only if event exists (not for drag and drop)
      if (event) {
        this.clearFileUpload(event);
      }
    },
    error: (error) => {
      this.isUploadingKikF1 = false;
      console.error('KIK#F1 upload error:', error);
      
      this.messageService.add({
        severity: 'error',
        summary: 'Upload Failed',
        detail: error.error?.message || 'Failed to save KIK#F1 data',
        life: 5000
      });
      
      // Clear file upload only if event exists
      if (event) {
        this.clearFileUpload(event);
      }
    }
  });
}

  /**
   * Close KIK#F1 upload dialog
   */
  closeKikF1UploadDialog(): void {
    this.showKikF1Upload = false;
    this.uploadResults = null;
  }
}